import { HttpEvent, HttpHandler, HttpRequest } from '@angular/common/http';
import { from } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import * as i0 from "@angular/core";
let pendingToken = null;
// getToken() has AWS's own ~2s internal timeout, so normal slowness already
// resolves on its own. This outer guard sits above that and only matters if the
// SDK promise never settles at all, which would otherwise wedge `pendingToken`
// and block every Angular HTTP request indefinitely. On timeout we proceed
// without a fresh token, the same as a refresh failure.
const TOKEN_TIMEOUT_MS = 3000;
function ensureToken() {
    if (pendingToken) {
        return pendingToken;
    }
    pendingToken = new Promise((resolve) => {
        let settled = false;
        let timer;
        // Returns true only for the call that actually settles the promise, so a
        // loser (e.g. getToken settling after the timeout already won) stays
        // silent instead of logging a misleading message.
        const settle = (value) => {
            if (settled)
                return false;
            settled = true;
            clearTimeout(timer);
            resolve(value);
            return true;
        };
        timer = setTimeout(() => {
            if (settle(null)) {
                console.warn('WAF token refresh timed out; proceeding without a fresh token');
            }
        }, TOKEN_TIMEOUT_MS);
        AwsWafIntegration
            .getToken()
            .then((token) => settle(token))
            .catch((err) => {
            if (settle(null)) {
                console.warn('WAF token refresh failed', err);
            }
        });
    });
    pendingToken.finally(() => {
        pendingToken = null;
    });
    return pendingToken;
}
export class WafTokenInterceptor {
    intercept(req, next) {
        if (typeof AwsWafIntegration === 'undefined') {
            return next.handle(req);
        }
        // WAF only protects the application origin and is the sole consumer of
        // the token. Never attach it to cross-origin requests (e.g. the static
        // CDN), whose CORS allow-list omits x-aws-waf-token and would reject the
        // OPTIONS preflight, breaking those requests for everyone. Resolve the
        // URL the same way the browser does so protocol-relative CDN URLs like
        // //static.ycharts.com are correctly classified as cross-origin.
        //
        // Same-origin /media/ requests are excluded too: the server redirects
        // them to the static CDN, and the custom header would force a CORS preflight
        // on the redirected cross-origin hop, which the CDN would reject.
        let attachToken;
        try {
            const url = new URL(req.url, window.location.origin);
            attachToken = url.origin === window.location.origin && !url.pathname.startsWith('/media/');
        }
        catch {
            attachToken = false;
        }
        if (!attachToken) {
            return next.handle(req);
        }
        return from(ensureToken()).pipe(switchMap((token) => {
            const outgoing = token ? req.clone({ setHeaders: { 'x-aws-waf-token': token } }) : req;
            return next.handle(outgoing);
        }));
    }
}
WafTokenInterceptor.ɵfac = function WafTokenInterceptor_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || WafTokenInterceptor)(); };
WafTokenInterceptor.ɵprov = /*@__PURE__*/ i0.ɵɵdefineInjectable({ token: WafTokenInterceptor, factory: WafTokenInterceptor.ɵfac });
